/* =====================================================================
 *  PARENT PORTAL — Student ID is the entire auth
 * ===================================================================== */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { schoolSystemApi, downloadPdf } from "@/lib/schoolSystemApi";
import {
  GraduationCap, ArrowLeft, Printer, ChatCircleDots, CalendarBlank,
} from "@phosphor-icons/react";
import { F, SelectField } from "./shared";
import { SchoolBrandingHeader } from "./BrandingAndPosition";

export default function ParentPortal() {
  const { studentId } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const parentEmail = searchParams.get("parent_email") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);   // {code, message, cost, remaining, admin_whatsapp}
  const [tab, setTab] = useState("overview");
  const [msg, setMsg] = useState({ kind: "general", subject: "", body: "" });
  const [appt, setAppt] = useState({ preferred_date: "", preferred_time: "", purpose: "", meeting_type: "physical" });
  const [reportOpen, setReportOpen] = useState(null);

  useEffect(() => {
    schoolSystemApi.parentView(studentId, parentEmail)
      .then((d) => { setData(d); setError(null); })
      .catch((err) => {
        const d = err?.response?.data?.detail;
        if (err?.response?.status === 401) {
          setError({ code: "auth_required",
                      message: typeof d === "object" ? d.message : "Sign in with your email to open the student portal." });
        } else if (err?.response?.status === 402) {
          setError({
            code: "insufficient_credits",
            message: typeof d === "object" ? d.message : "You are out of credits.",
            cost: typeof d === "object" ? d.cost : 0,
            remaining: typeof d === "object" ? d.remaining : 0,
            admin_whatsapp: typeof d === "object" ? d.admin_whatsapp : "",
          });
        } else if (err?.response?.status === 404) {
          setError({ code: "invalid_id", message: "Invalid Student ID" });
        } else {
          setError({ code: "error", message: (typeof d === "string" ? d : "Failed to load portal") });
        }
      });
  }, [studentId, parentEmail]);

  if (error) return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="parent-portal-error">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-heading font-black">
          {error.code === "insufficient_credits" ? "You have run out of credits"
            : error.code === "auth_required" ? "Sign in to open the portal"
            : error.code === "invalid_id" ? "Invalid Student ID"
            : "Cannot open portal"}
        </h1>
        <p className="text-white/70 text-sm">{error.message}</p>
        {error.code === "insufficient_credits" && (
          <>
            {typeof error.cost === "number" && (
              <p className="text-xs text-white/50">
                This costs {error.cost} credit{error.cost === 1 ? "" : "s"} · You have {error.remaining}.
              </p>
            )}
            {error.admin_whatsapp && (
              <a href={error.admin_whatsapp} target="_blank" rel="noopener noreferrer"
                 data-testid="parent-buy-credits-cta"
                 className="inline-block px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
                Buy Credits from Tainapp on Whatsapp
              </a>
            )}
          </>
        )}
        {error.code === "auth_required" && (
          <a href="/auth" data-testid="parent-signin-cta"
             className="inline-block px-4 py-2 rounded-md bg-sky-500 hover:bg-sky-600 text-black font-bold">
            Sign in / Register
          </a>
        )}
      </div>
    </div>
  );
  if (!data) return <div className="p-8 text-white/60">Loading…</div>;
  const s = data.student; const school = data.school;

  const sendMessage = async () => {
    if (!msg.subject || !msg.body) return toast.error("Subject and body required");
    try {
      await schoolSystemApi.parentSendMessage({ school_id: s.school_id, student_id: s.student_id, ...msg });
      toast.success("Message submitted"); setMsg({ kind: "general", subject: "", body: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const requestAppt = async () => {
    if (!appt.preferred_date || !appt.purpose) return toast.error("Date and purpose required");
    try {
      await schoolSystemApi.requestAppointment({
        school_id: s.school_id, student_id: s.student_id, initiator: "parent", ...appt,
      });
      toast.success("Appointment requested");
      setAppt({ preferred_date: "", preferred_time: "", purpose: "", meeting_type: "physical" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-4" data-testid="parent-portal">
      <Link to="/school-system" className="text-white/60 hover:text-white text-sm flex items-center gap-1">
        <ArrowLeft size={14} weight="bold" /> School System
      </Link>
      <SchoolBrandingHeader schoolId={s.school_id} portal="student" studentId={s.student_id} />
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-white/60">{school?.name}</p>
            <h1 className="font-heading text-2xl font-black">{s.student_name}</h1>
            <p className="text-sm text-white/70">{s.grade} · Student ID <b data-testid="parent-portal-sid">{s.student_id}</b></p>
          </div>
          <GraduationCap size={44} weight="fill" className="text-sky-400"/>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#121212] border border-white/10 flex-wrap gap-1 p-1 h-auto w-full justify-start">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="records">Report card</TabsTrigger>
          <TabsTrigger value="lesson-notes" data-testid="parent-tab-notes">Lesson notes</TabsTrigger>
          <TabsTrigger value="tests" data-testid="parent-tab-tests">Test questions</TabsTrigger>
          <TabsTrigger value="marking" data-testid="parent-tab-marking">Marking schemes</TabsTrigger>
          <TabsTrigger value="results" data-testid="parent-tab-results">Test results</TabsTrigger>
          <TabsTrigger value="performance" data-testid="parent-tab-perf">Performance</TabsTrigger>
          <TabsTrigger value="homework">Homework</TabsTrigger>
          <TabsTrigger value="fees">Fees</TabsTrigger>
          <TabsTrigger value="communication">Message school</TabsTrigger>
          <TabsTrigger value="appointment">Book appointment</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-3 space-y-2">
          <Card className="bg-[#121212] border-white/10"><CardContent className="p-3">
            <p className="text-sm">Attendance days recorded: <b>{data.attendance.length}</b></p>
            <p className="text-sm">Subjects: <b>{data.subjects.length}</b></p>
            <p className="text-sm">Recent behaviour entries: <b>{data.behaviour.length}</b></p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="records" className="mt-3 space-y-2">
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="open-report-card" onClick={() => setReportOpen({ year: String(new Date().getFullYear()), term: "Term 1" })} className="bg-sky-600 hover:bg-sky-700 gap-2">
              <Printer size={16} weight="bold"/> View / print report card
            </Button>
          </div>
          {data.records.map((r) => (
            <Card key={r.record_id} className="bg-[#121212] border-white/10">
              <CardContent className="p-3 text-sm">
                <p><b>{r.subject_id}</b> — CA {r.ca_score}, Exam {r.exam_score}, Total {r.total}, Grade {r.grade_letter}</p>
                <p className="text-xs text-white/60">{r.academic_year} · {r.term}</p>
              </CardContent>
            </Card>
          ))}
          {data.records.length === 0 && <p className="text-white/50 text-sm">No approved records yet.</p>}
        </TabsContent>

        <TabsContent value="homework" className="mt-3 space-y-2">
          <HomeworkTab data={data} studentId={s.student_id} schoolId={s.school_id}/>
        </TabsContent>

        <TabsContent value="lesson-notes" className="mt-3">
          <LessonNotesTab studentId={s.student_id}/>
        </TabsContent>
        <TabsContent value="tests" className="mt-3">
          <TestQuestionsTab studentId={s.student_id}/>
        </TabsContent>
        <TabsContent value="marking" className="mt-3">
          <MarkingSchemesTab studentId={s.student_id}/>
        </TabsContent>
        <TabsContent value="results" className="mt-3">
          <TestResultsTab data={data}/>
        </TabsContent>
        <TabsContent value="performance" className="mt-3">
          <PerformanceTab studentId={s.student_id} schoolId={s.school_id}/>
        </TabsContent>

        <TabsContent value="fees" className="mt-3 space-y-2">
          <ParentFeesTab studentId={s.student_id} legacy={data.fees || []} />
        </TabsContent>

        <TabsContent value="communication" className="mt-3 space-y-2 max-w-lg">
          <SelectField label="Kind" value={msg.kind} onChange={(v) => setMsg({...msg, kind: v})} options={[
            {value:"general",label:"General message"}, {value:"complaint",label:"Complaint"},
            {value:"suggestion",label:"Suggestion"}, {value:"leave_request",label:"Leave request"},
            {value:"emergency",label:"Emergency"}, {value:"report",label:"Report"}]}/>
          <F label="Subject"><Input data-testid="parent-msg-subject" value={msg.subject} onChange={(e)=>setMsg({...msg,subject:e.target.value})} className="bg-white/5 border-white/10"/></F>
          <F label="Message"><Textarea data-testid="parent-msg-body" rows={4} value={msg.body} onChange={(e)=>setMsg({...msg,body:e.target.value})} className="bg-white/5 border-white/10"/></F>
          <Button data-testid="parent-msg-send" onClick={sendMessage} className="bg-sky-600 hover:bg-sky-700 gap-1"><ChatCircleDots size={14} weight="bold"/> Send</Button>
        </TabsContent>

        <TabsContent value="appointment" className="mt-3 space-y-2 max-w-lg">
          <F label="Preferred date"><Input data-testid="appt-date" type="date" value={appt.preferred_date} onChange={(e)=>setAppt({...appt,preferred_date:e.target.value})} className="bg-white/5 border-white/10"/></F>
          <F label="Preferred time"><Input data-testid="appt-time" type="time" value={appt.preferred_time} onChange={(e)=>setAppt({...appt,preferred_time:e.target.value})} className="bg-white/5 border-white/10"/></F>
          <F label="Purpose"><Input data-testid="appt-purpose" value={appt.purpose} onChange={(e)=>setAppt({...appt,purpose:e.target.value})} className="bg-white/5 border-white/10"/></F>
          <SelectField label="Meeting type" value={appt.meeting_type} onChange={(v)=>setAppt({...appt,meeting_type:v})} options={[{value:"physical",label:"Physical"},{value:"virtual",label:"Virtual"}]}/>
          <Button data-testid="appt-request" onClick={requestAppt} className="bg-sky-600 hover:bg-sky-700 gap-1"><CalendarBlank size={14} weight="fill"/> Request</Button>
        </TabsContent>
      </Tabs>

      {reportOpen && (
        <ReportCardDialog
          school={school} student={s}
          academic_year={reportOpen.year} term={reportOpen.term}
          onClose={() => setReportOpen(null)}
        />
      )}
    </div>
  );
}

function ParentFeesTab({ studentId, legacy }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    schoolSystemApi.feesForStudent(studentId)
      .then(setData)
      .catch(() => setData({ fees: [], totals: {} }));
  }, [studentId]);
  const fees = data ? (data.fees || []) : legacy;
  if (!data) return <p className="text-white/50 text-sm">Loading fees…</p>;
  if (fees.length === 0) {
    return <p className="text-white/50 text-sm">No fee records yet.</p>;
  }
  const totals = data.totals || {};
  return (
    <div className="space-y-3" data-testid="parent-fees">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="text-xs text-white/60">
          <span className="mr-3">Owed <b className="text-white">GHS {(totals.owed || 0).toFixed(2)}</b></span>
          <span className="mr-3">Paid <b className="text-emerald-300">GHS {(totals.paid || 0).toFixed(2)}</b></span>
          <span>Outstanding <b className="text-rose-300">GHS {(totals.outstanding || 0).toFixed(2)}</b></span>
        </div>
        <Button size="sm" variant="outline"
                data-testid="parent-fee-statement-pdf"
                onClick={() => downloadPdf(
                  schoolSystemApi.studentStatementPdfPath(studentId),
                  `fee-statement-${studentId}.pdf`)}
                className="border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200">
          Full statement PDF
        </Button>
      </div>
      {fees.map((f) => (
        <Card key={f.fee_id} className="bg-[#121212] border-white/10">
          <CardContent className="p-3 text-sm space-y-1">
            <p className="flex items-center justify-between gap-2">
              <span><b>{f.fee_type}</b> — {f.academic_year} · {f.term}</span>
              <Badge className={(Number(f.amount_paid || 0) >= Number(f.amount_due || 0) + Number(f.arrears || 0))
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                : "bg-rose-500/20 text-rose-300 border-rose-500/40"}>
                {(Number(f.amount_paid || 0) >= Number(f.amount_due || 0) + Number(f.arrears || 0)) ? "Cleared" : "Outstanding"}
              </Badge>
            </p>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div><span className="text-white/50">Term fee: </span><b>GHS {Number(f.amount_due||0).toFixed(2)}</b></div>
              <div><span className="text-white/50">Arrears: </span><b>GHS {Number(f.arrears||0).toFixed(2)}</b></div>
              <div><span className="text-white/50">Paid: </span><b className="text-emerald-300">GHS {Number(f.amount_paid||0).toFixed(2)}</b></div>
              <div><span className="text-white/50">Balance: </span><b className="text-rose-300">
                GHS {Math.max((Number(f.amount_due||0) + Number(f.arrears||0)) - Number(f.amount_paid||0), 0).toFixed(2)}
              </b></div>
            </div>
            {(f.receipts || []).length > 0 && (
              <div className="pt-2 border-t border-white/10 mt-1">
                <p className="text-[11px] uppercase tracking-widest text-white/50">Receipts</p>
                <div className="mt-1 space-y-1">
                  {(f.receipts || []).map((rec) => (
                    <div key={rec.receipt_id} className="flex items-center justify-between gap-2 text-xs">
                      <span>{rec.receipt_number || rec.receipt_id} · {(rec.issued_at || "").slice(0,10)} · GHS {Number(rec.amount || 0).toFixed(2)}</span>
                      <Button size="sm" variant="outline"
                              onClick={() => downloadPdf(
                                schoolSystemApi.feeReceiptPdfPath(f.fee_id, rec.receipt_id),
                                `receipt-${rec.receipt_number || rec.receipt_id}.pdf`)}
                              className="border-white/20 bg-transparent hover:bg-white/5 h-7 px-2 text-[11px]">
                        PDF
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {f.payment_instructions && <p className="text-[11px] text-white/50 mt-1">{f.payment_instructions}</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


function ReportCardDialog({ school, student, academic_year, term, onClose }) {
  const [rc, setRc] = useState(null);
  useEffect(() => {
    schoolSystemApi.reportCard(student.school_id, student.student_id, academic_year, term, student.student_id)
      .then(setRc)
      .catch((e) => { toast.error(e.response?.data?.detail || "Failed"); onClose(); });
  }, [student, academic_year, term, onClose]);
  const doPrint = () => window.print();
  if (!rc) return null;
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white text-black max-w-3xl print:shadow-none max-h-[92vh] overflow-y-auto">
        <div className="p-2 print:p-0" data-testid="report-card">
          <div className="text-center border-b border-gray-300 pb-3">
            <h1 className="text-2xl font-black">{rc.school?.name}</h1>
            <p className="text-xs text-gray-600">Report Card · {rc.academic_year} · {rc.term}</p>
            {(rc.term_dates?.reopening_date || rc.term_dates?.closing_date) && (
              <p className="text-[11px] text-gray-500 mt-1" data-testid="rc-term-dates">
                Term closes: {rc.term_dates?.closing_date || "—"} · Reopens: {rc.term_dates?.reopening_date || "—"}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm mt-3">
            <p><b>Student:</b> {rc.student.student_name}</p>
            <p><b>Student ID:</b> {rc.student.student_id}</p>
            <p><b>Class:</b> {rc.student.grade}</p>
            <p><b>Number on Roll:</b> {rc.totals.number_on_roll}</p>
            <p><b>Attendance:</b> {rc.attendance.present}P / {rc.attendance.absent}A / {rc.attendance.late}L ({rc.attendance.percent}%)</p>
            <p><b>Overall Position:</b> {rc.totals.overall_position || "—"} of {rc.totals.number_on_roll}</p>
          </div>
          <table className="w-full text-sm border-collapse mt-3">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-1 text-left">Subject</th>
                <th className="border p-1">CA</th>
                <th className="border p-1">Exam</th>
                <th className="border p-1">Total</th>
                <th className="border p-1">Grade</th>
                <th className="border p-1 text-left">Descriptor</th>
                <th className="border p-1">Position</th>
              </tr>
            </thead>
            <tbody>
              {rc.records.map((r) => {
                const subj = (rc.subjects.find(x=>x.subject_id===r.subject_id)||{}).name || r.subject_id;
                return (
                  <tr key={r.record_id}>
                    <td className="border p-1">{subj}</td>
                    <td className="border p-1 text-center">{r.ca_score}</td>
                    <td className="border p-1 text-center">{r.exam_score}</td>
                    <td className="border p-1 text-center">{r.total}</td>
                    <td className="border p-1 text-center">{r.grade_letter}</td>
                    <td className="border p-1">{r.descriptor || r.remark || ""}</td>
                    <td className="border p-1 text-center">{r.subject_position ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr className="font-bold bg-gray-50">
              <td className="border p-1">Totals</td><td colSpan={2}></td>
              <td className="border p-1 text-center">{rc.totals.total}</td>
              <td className="border p-1 text-center">{rc.totals.overall_grade || "—"}</td>
              <td className="border p-1">{rc.totals.overall_descriptor || `Avg ${rc.totals.average}`}</td>
              <td className="border p-1 text-center">Avg {rc.totals.average}</td>
            </tr></tfoot>
          </table>
          <div className="grid grid-cols-2 gap-3 text-sm mt-3">
            <p><b>Conduct:</b> {rc.behaviour?.conduct || "—"}</p>
            <p><b>Attitude:</b> {rc.behaviour?.attitude || "—"}</p>
            <p><b>Interest:</b> {rc.behaviour?.interest || "—"}</p>
            <p><b>Discipline:</b> {rc.behaviour?.discipline || "—"}</p>
          </div>
          <div className="mt-4 text-sm border-t border-gray-300 pt-2">
            <p><b>Fees:</b> Due ${rc.fee_summary.total_due} · Paid ${rc.fee_summary.total_paid} · Outstanding ${rc.fee_summary.outstanding}</p>
          </div>
          {rc.remarks && (
            <div className="mt-4 space-y-2" data-testid="report-card-remarks">
              <div className="border border-gray-300 rounded p-2 bg-gray-50">
                <p className="text-xs uppercase tracking-widest text-gray-500 font-bold">Class Teacher's Remark</p>
                <p className="text-sm text-gray-800 mt-1">{rc.remarks.class_teacher}</p>
              </div>
              <div className="border border-gray-300 rounded p-2 bg-gray-50">
                <p className="text-xs uppercase tracking-widest text-gray-500 font-bold">Headteacher / Principal's Remark</p>
                <p className="text-sm text-gray-800 mt-1">{rc.remarks.headteacher}</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-6 mt-6 text-sm">
            <div><div className="border-t border-black mt-6 pt-1">Class Teacher</div></div>
            <div><div className="border-t border-black mt-6 pt-1">Headteacher / Principal</div></div>
          </div>
        </div>
        <DialogFooter className="print:hidden">
          <Button variant="outline" onClick={onClose} className="border-gray-300">Close</Button>
          <Button data-testid="print-report-btn" onClick={doPrint} className="bg-sky-600 hover:bg-sky-700 gap-2"><Printer size={14} weight="bold"/> Print / Save PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------------------------------------------------------ *
 *  Phase 3c tabs                                                     *
 * ------------------------------------------------------------------ */

function LessonNotesTab({ studentId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { schoolSystemApi.parentLessonNotes(studentId).then(r => setRows(r.items || [])).catch(() => setRows([])); }, [studentId]);
  if (rows === null) return <p className="text-white/60 text-sm">Loading…</p>;
  if (rows.length === 0) return <p className="text-white/50 text-sm">No lesson notes approved yet.</p>;
  return (
    <div className="space-y-2" data-testid="parent-lesson-notes">
      {rows.map(n => (
        <Card key={n.note_id} className="bg-[#121212] border-white/10"><CardContent className="p-3">
          <p className="font-heading font-bold">{n.title}</p>
          <p className="text-xs text-white/60">{n.subject} · {n.week ? `Week ${n.week}` : ""}{n.strand ? ` · ${n.strand}` : ""}</p>
          <div className="text-sm text-white/85 mt-2 whitespace-pre-wrap">{n.body || n.content}</div>
        </CardContent></Card>
      ))}
    </div>
  );
}

function HomeworkTab({ data, studentId, schoolId }) {
  const [scores, setScores] = useState([]);
  const [schemes, setSchemes] = useState({}); // homework_id -> scheme|null
  useEffect(() => {
    schoolSystemApi.parentHomeworkScores(studentId)
      .then(r => setScores(r.items || []))
      .catch(() => setScores([]));
  }, [studentId]);
  // Lazily fetch marking scheme per homework only when the user opens the details panel
  const fetchScheme = async (hwId) => {
    if (schemes[hwId] !== undefined) return;
    try {
      const r = await schoolSystemApi.homeworkGetScheme(hwId);
      setSchemes((prev) => ({ ...prev, [hwId]: r.scheme || null }));
    } catch { setSchemes((prev) => ({ ...prev, [hwId]: null })); }
  };
  const homework = data.homework || [];
  if (homework.length === 0) {
    return <p className="text-white/50 text-sm">No approved homework yet.</p>;
  }
  return (
    <div className="space-y-2" data-testid="parent-homework">
      {homework.map((h) => {
        const label = h.hw_label || (h.hw_number ? `HW-${String(h.hw_number).padStart(4,"0")}` : "HW");
        const myScore = scores.find(s => s.homework_id === h.homework_id);
        return (
          <Card key={h.homework_id} className="bg-[#121212] border-white/10">
            <CardContent className="p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.28em] uppercase text-amber-300 font-bold">{label}</p>
                  <p className="font-heading font-bold">{h.title}</p>
                  <p className="text-xs text-white/60">Due {h.due_date || "—"} · {h.kind}</p>
                </div>
                {myScore && (
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                    Score: {myScore.score}/{myScore.max_score} ({myScore.percentage}%)
                  </Badge>
                )}
              </div>
              <p className="text-sm text-white/85 whitespace-pre-wrap">{h.description}</p>
              {myScore?.remarks && (
                <p className="text-xs text-white/70">
                  <b>Teacher remark:</b> {myScore.remarks}
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline"
                        data-testid={`parent-hw-pdf-${h.homework_id}`}
                        onClick={() => downloadPdf(
                          schoolSystemApi.homeworkPdfPath(h.homework_id),
                          `${label}.pdf`)}
                        className="border-white/20 bg-transparent hover:bg-white/5">
                  Download PDF
                </Button>
                <ParentSchemeButton
                  hw={h}
                  label={label}
                  loaded={schemes[h.homework_id]}
                  onLoad={() => fetchScheme(h.homework_id)}
                />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ParentSchemeButton({ hw, label, loaded, onLoad }) {
  const [open, setOpen] = useState(false);
  const openHandler = () => { setOpen(true); onLoad && onLoad(); };
  const hasScheme = loaded && (loaded.approved || loaded.status === "approved");
  return (
    <>
      <Button size="sm" variant="outline"
              data-testid={`parent-hw-scheme-${hw.homework_id}`}
              onClick={openHandler}
              className="border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200">
        Marking scheme
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0e0e0e] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Marking scheme · {label}</DialogTitle>
          </DialogHeader>
          {loaded === undefined ? (
            <p className="text-white/60 text-sm">Loading…</p>
          ) : !hasScheme ? (
            <p className="text-white/60 text-sm">
              The teacher hasn't published a marking scheme for this homework yet.
              Check back once the head-teacher has approved it.
            </p>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              <div className="flex justify-end">
                <Button size="sm" variant="outline"
                        onClick={() => downloadPdf(
                          schoolSystemApi.homeworkSchemePdfPath(hw.homework_id),
                          `${label}-marking-scheme.pdf`)}
                        className="border-white/20 bg-transparent hover:bg-white/5">
                  Download PDF
                </Button>
              </div>
              <p className="text-xs text-white/60">Total marks: {loaded.total_marks}</p>
              <ol className="space-y-2 mt-2 text-sm">
                {(loaded.content?.items || []).map((it, i) => (
                  <li key={i} className="p-2 rounded bg-white/[0.03]">
                    <p className="font-medium">{i+1}. {it.q}</p>
                    <p className="text-xs text-emerald-300 mt-1"><b>Expected:</b> {it.expected_answer}</p>
                    {it.marking_criteria && (
                      <p className="text-xs text-white/60 mt-1"><b>Criteria:</b> {it.marking_criteria}</p>
                    )}
                    <p className="text-[10px] text-amber-300 mt-1">Marks: {it.marks}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


function TestQuestionsTab({ studentId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { schoolSystemApi.parentTests(studentId).then(r => setRows(r.items || [])).catch(() => setRows([])); }, [studentId]);
  if (rows === null) return <p className="text-white/60 text-sm">Loading…</p>;
  if (rows.length === 0) return <p className="text-white/50 text-sm">No test questions approved yet.</p>;
  return (
    <div className="space-y-2" data-testid="parent-tests">
      {rows.map(t => (
        <Card key={t.test_id} className="bg-[#121212] border-white/10"><CardContent className="p-3">
          <p className="font-heading font-bold">{t.title || `${t.subject} — ${t.assessment_type}`}</p>
          <p className="text-xs text-white/60">{t.subject} · {t.assessment_type} · Total {t.total_marks}</p>
          {Array.isArray(t.questions) && (
            <ol className="text-sm text-white/85 mt-2 list-decimal ml-5 space-y-1">
              {t.questions.map((q, i) => <li key={i}><b>[{q.marks}m]</b> {q.question || q.text}</li>)}
            </ol>
          )}
        </CardContent></Card>
      ))}
    </div>
  );
}

function MarkingSchemesTab({ studentId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { schoolSystemApi.parentMarkingSchemes(studentId).then(r => setRows(r.items || [])).catch(() => setRows([])); }, [studentId]);
  if (rows === null) return <p className="text-white/60 text-sm">Loading…</p>;
  if (rows.length === 0) return <p className="text-white/50 text-sm">No marking schemes approved yet.</p>;
  return (
    <div className="space-y-2" data-testid="parent-marking">
      {rows.map(m => (
        <Card key={m.scheme_id} className="bg-[#121212] border-white/10"><CardContent className="p-3">
          <p className="font-heading font-bold">{m.title || m.subject}</p>
          <p className="text-xs text-white/60">{m.subject} · {m.assessment_type}</p>
          {Array.isArray(m.answers) && (
            <ol className="text-sm text-white/85 mt-2 list-decimal ml-5 space-y-1">
              {m.answers.map((a, i) => <li key={i}><b>[{a.marks}m]</b> {a.answer}</li>)}
            </ol>
          )}
        </CardContent></Card>
      ))}
    </div>
  );
}

function TestResultsTab({ data }) {
  const rows = (data.student_results || data.records || []);
  if (rows.length === 0) return <p className="text-white/50 text-sm">No test results yet.</p>;
  return (
    <div className="overflow-x-auto" data-testid="parent-results">
      <table className="min-w-full text-sm">
        <thead className="text-left text-white/60 uppercase text-xs">
          <tr>
            <th className="py-2 pr-3">Subject</th>
            <th className="py-2 pr-3">Assessment</th>
            <th className="py-2 pr-3">Score</th>
            <th className="py-2 pr-3">Total</th>
            <th className="py-2 pr-3">Grade</th>
            <th className="py-2 pr-3">Remark</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.result_id || r.record_id || i} className="border-t border-white/5">
              <td className="py-2 pr-3">{r.subject || r.subject_id}</td>
              <td className="py-2 pr-3">{r.assessment_type || `${r.academic_year || ""} ${r.term || ""}`}</td>
              <td className="py-2 pr-3">{r.score ?? r.total ?? "—"}</td>
              <td className="py-2 pr-3">{r.total_marks ?? "—"}</td>
              <td className="py-2 pr-3">{r.grade_letter || "—"}</td>
              <td className="py-2 pr-3">{r.remark || r.descriptor || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PerformanceTab({ studentId, schoolId }) {
  const [perf, setPerf] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    schoolSystemApi.performanceRanked(studentId, { school_id: schoolId })
      .then(setPerf)
      .catch(e => setErr(e.response?.data?.detail || "Failed"));
  }, [studentId, schoolId]);
  if (err) return <p className="text-red-400 text-sm">{err}</p>;
  if (!perf) return <p className="text-white/60 text-sm">Loading…</p>;
  const rows = perf.per_subject || [];
  const o = perf.overall || {};
  return (
    <div className="space-y-3" data-testid="parent-performance">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-white/60 text-xs">Approved test results only. Class rank is calculated across the student's grade.</p>
        <Button
          onClick={() => {
            const qp = new URLSearchParams({ school_id: schoolId });
            downloadPdf(
              `/school-system/students/${studentId}/performance/pdf?${qp}`,
              `performance-${studentId}.pdf`,
            ).catch((e) =>
              toast.error(e?.response?.data?.detail || e.message || "PDF failed"));
          }}
          data-testid="parent-perf-pdf"
          size="sm"
          className="bg-sky-500 hover:bg-sky-600 text-black font-bold">
          Generate PDF
        </Button>
      </div>
      <Card className="bg-[#121212] border-white/10"><CardContent className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        <p><b>Average:</b> {o.average}%</p>
        <p><b>Grade:</b> {o.grade_letter || "—"}</p>
        <p><b>Class position:</b> {o.class_position ?? "—"} of {o.class_size ?? "—"}</p>
        <p><b>Descriptor:</b> {o.descriptor || "—"}</p>
        {o.class_teacher_remark && <p className="col-span-2 md:col-span-4"><b>Class teacher:</b> {o.class_teacher_remark}</p>}
        {o.headteacher_remark && <p className="col-span-2 md:col-span-4"><b>Headteacher:</b> {o.headteacher_remark}</p>}
      </CardContent></Card>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-white/60 uppercase text-xs">
            <tr>
              <th className="py-2 pr-3">Subject</th>
              <th className="py-2 pr-3">Assessments</th>
              <th className="py-2 pr-3">%</th>
              <th className="py-2 pr-3">Grade</th>
              <th className="py-2 pr-3">Class position</th>
              <th className="py-2 pr-3">Remark</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (<tr><td colSpan={6} className="py-2 text-white/50">No approved results yet.</td></tr>)}
            {rows.map(r => (
              <tr key={r.subject} className="border-t border-white/5" data-testid={`perf-row-${r.subject}`}>
                <td className="py-2 pr-3">{r.subject}</td>
                <td className="py-2 pr-3">{r.assessments}</td>
                <td className="py-2 pr-3">{r.percentage}%</td>
                <td className="py-2 pr-3">{r.grade_letter}</td>
                <td className="py-2 pr-3">{r.class_position ?? "—"} / {r.class_size}</td>
                <td className="py-2 pr-3">{r.remark || r.descriptor || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
